import Heading from "@tiptap/extension-heading";
import Image from "@tiptap/extension-image";
import TextAlign from "@tiptap/extension-text-align";
import { Placeholder } from "@tiptap/extensions";
import type { EditorView } from "@tiptap/pm/view";
import {
  Editor,
  EditorContent,
  type EditorOptions,
  useEditor,
  useEditorState,
} from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { Fragment, type ReactNode, useEffect, useState } from "react";

import IconMaterialSymbolsBackspaceOutline from "~icons/material-symbols/backspace-outline";
import IconMaterialSymbolsFormatAlignCenter from "~icons/material-symbols/format-align-center";
import IconMaterialSymbolsFormatAlignLeft from "~icons/material-symbols/format-align-left";
import IconMaterialSymbolsFormatAlignRight from "~icons/material-symbols/format-align-right";
import IconMaterialSymbolsFormatBold from "~icons/material-symbols/format-bold";
import IconMaterialSymbolsFormatH1 from "~icons/material-symbols/format-h1";
import IconMaterialSymbolsFormatH2 from "~icons/material-symbols/format-h2";
import IconMaterialSymbolsFormatH3 from "~icons/material-symbols/format-h3";
import IconMaterialSymbolsFormatItalic from "~icons/material-symbols/format-italic";
import IconMaterialSymbolsFormatListBulleted from "~icons/material-symbols/format-list-bulleted";
import IconMaterialSymbolsFormatListNumbered from "~icons/material-symbols/format-list-numbered";
import IconMaterialSymbolsFormatQuote from "~icons/material-symbols/format-quote";
import IconMaterialSymbolsFormatStrikethrough from "~icons/material-symbols/format-strikethrough";
import IconMaterialSymbolsHorizontalRule from "~icons/material-symbols/horizontal-rule";
import IconMaterialSymbolsRedo from "~icons/material-symbols/redo";
import IconMaterialSymbolsUndo from "~icons/material-symbols/undo";

import { focusStyles } from "@beweegbaas/ui/lib/focus-styles";
import { cn } from "@beweegbaas/ui/lib/utils";
import { Button } from "./button";

export type TiptapTool =
  | "heading1"
  | "heading2"
  | "heading3"
  | "bold"
  | "italic"
  | "strike"
  | "bulletList"
  | "orderedList"
  | "alignLeft"
  | "alignCenter"
  | "alignRight"
  | "blockquote"
  | "horizontalRule"
  | "undo"
  | "redo"
  | "clearFormatting";

// Toolbar layout: tools render in this group order, separators only between non-empty groups.
const TOOL_GROUPS: readonly (readonly TiptapTool[])[] = [
  ["heading1", "heading2", "heading3"],
  ["bold", "italic", "strike"],
  ["bulletList", "orderedList"],
  ["alignLeft", "alignCenter", "alignRight"],
  ["blockquote", "horizontalRule"],
  ["undo", "redo", "clearFormatting"],
];

const ALL_TOOLS: readonly TiptapTool[] = TOOL_GROUPS.flat();

type TiptapEditorProps = {
  id?: string;
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  editorOptions?: Partial<EditorOptions>;
  className?: string;
  /** Extra classes for the editable content area itself (e.g. a taller min-height). */
  contentClassName?: string;
  /** Which toolbar buttons to show. Defaults to all of them. */
  tools?: readonly TiptapTool[];
  /** Accept pasted/dropped images as inline base64 data URIs in the HTML output. */
  allowImages?: boolean;
};

const MAX_IMAGE_DIMENSION = 1600;

// Downscale + re-encode before inlining: a raw 4K screenshot as base64 is several MB.
async function imageFileToDataUri(file: File): Promise<string> {
  const bitmap = await createImageBitmap(file);
  const scale = Math.min(1, MAX_IMAGE_DIMENSION / Math.max(bitmap.width, bitmap.height));
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(bitmap.width * scale);
  canvas.height = Math.round(bitmap.height * scale);
  canvas.getContext("2d")?.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  bitmap.close();
  return canvas.toDataURL("image/jpeg", 0.85);
}

async function insertImageFile(view: EditorView, file: File, pos?: number) {
  const src = await imageFileToDataUri(file);
  const node = view.state.schema.nodes.image?.createAndFill({ src });
  if (!node) return;
  const tr =
    pos === undefined ? view.state.tr.replaceSelectionWith(node) : view.state.tr.insert(pos, node);
  view.dispatch(tr);
}

function imageFilesFrom(data: DataTransfer | null): File[] {
  return Array.from(data?.files ?? []).filter((file) => file.type.startsWith("image/"));
}

function ToolbarButton({
  isActive = false,
  onClick,
  disabled = false,
  children,
}: {
  isActive?: boolean;
  onClick: () => void;
  disabled?: boolean;
  children: ReactNode;
}) {
  return (
    <Button
      type="button"
      variant={isActive ? "secondary" : "ghost"}
      onClick={onClick}
      className={"rounded"}
      disabled={disabled}
      size="icon"
    >
      {children}
    </Button>
  );
}

function MenuBar({
  editor,
  disabled,
  tools,
}: {
  editor: Editor;
  disabled: boolean;
  tools: readonly TiptapTool[];
}) {
  const editorState = useEditorState({
    editor,
    selector: (ctx) => ({
      // Headings
      isHeading1: ctx.editor.isActive("heading", { level: 1 }) ?? false,
      isHeading2: ctx.editor.isActive("heading", { level: 2 }) ?? false,
      isHeading3: ctx.editor.isActive("heading", { level: 3 }) ?? false,

      // Text formatting
      isBold: ctx.editor.isActive("bold") ?? false,
      isItalic: ctx.editor.isActive("italic") ?? false,
      isStrike: ctx.editor.isActive("strike") ?? false,

      // Lists
      isBulletList: ctx.editor.isActive("bulletList") ?? false,
      isOrderedList: ctx.editor.isActive("orderedList") ?? false,

      // Alignment
      isAlignLeft: ctx.editor.isActive("textAlign", { textAlign: "left" }) ?? false,
      isAlignCenter: ctx.editor.isActive("textAlign", { textAlign: "center" }) ?? false,
      isAlignRight: ctx.editor.isActive("textAlign", { textAlign: "right" }) ?? false,

      // Block elements
      isBlockquote: ctx.editor.isActive("blockquote") ?? false,

      // History
      canUndo: ctx.editor.can().chain().undo().run() ?? false,
      canRedo: ctx.editor.can().chain().redo().run() ?? false,
    }),
  });

  const toolButtons: Record<
    TiptapTool,
    { icon: ReactNode; isActive?: boolean; isDisabled?: boolean; onClick: () => void }
  > = {
    heading1: {
      icon: <IconMaterialSymbolsFormatH1 />,
      isActive: editorState.isHeading1,
      onClick: () => editor.chain().focus().toggleHeading({ level: 1 }).run(),
    },
    heading2: {
      icon: <IconMaterialSymbolsFormatH2 />,
      isActive: editorState.isHeading2,
      onClick: () => editor.chain().focus().toggleHeading({ level: 2 }).run(),
    },
    heading3: {
      icon: <IconMaterialSymbolsFormatH3 />,
      isActive: editorState.isHeading3,
      onClick: () => editor.chain().focus().toggleHeading({ level: 3 }).run(),
    },
    bold: {
      icon: <IconMaterialSymbolsFormatBold />,
      isActive: editorState.isBold,
      onClick: () => editor.chain().focus().toggleBold().run(),
    },
    italic: {
      icon: <IconMaterialSymbolsFormatItalic />,
      isActive: editorState.isItalic,
      onClick: () => editor.chain().focus().toggleItalic().run(),
    },
    strike: {
      icon: <IconMaterialSymbolsFormatStrikethrough />,
      isActive: editorState.isStrike,
      onClick: () => editor.chain().focus().toggleStrike().run(),
    },
    bulletList: {
      icon: <IconMaterialSymbolsFormatListBulleted />,
      isActive: editorState.isBulletList,
      onClick: () => editor.chain().focus().toggleBulletList().run(),
    },
    orderedList: {
      icon: <IconMaterialSymbolsFormatListNumbered />,
      isActive: editorState.isOrderedList,
      onClick: () => editor.chain().focus().toggleOrderedList().run(),
    },
    alignLeft: {
      icon: <IconMaterialSymbolsFormatAlignLeft />,
      isActive: editorState.isAlignLeft,
      onClick: () => editor.chain().focus().setTextAlign("left").run(),
    },
    alignCenter: {
      icon: <IconMaterialSymbolsFormatAlignCenter />,
      isActive: editorState.isAlignCenter,
      onClick: () => editor.chain().focus().setTextAlign("center").run(),
    },
    alignRight: {
      icon: <IconMaterialSymbolsFormatAlignRight />,
      isActive: editorState.isAlignRight,
      onClick: () => editor.chain().focus().setTextAlign("right").run(),
    },
    blockquote: {
      icon: <IconMaterialSymbolsFormatQuote />,
      isActive: editorState.isBlockquote,
      onClick: () => editor.chain().focus().toggleBlockquote().run(),
    },
    horizontalRule: {
      icon: <IconMaterialSymbolsHorizontalRule />,
      onClick: () => editor.chain().focus().setHorizontalRule().run(),
    },
    undo: {
      icon: <IconMaterialSymbolsUndo />,
      isDisabled: !editorState.canUndo || disabled,
      onClick: () => editor.chain().focus().undo().run(),
    },
    redo: {
      icon: <IconMaterialSymbolsRedo />,
      isDisabled: !editorState.canRedo || disabled,
      onClick: () => editor.chain().focus().redo().run(),
    },
    clearFormatting: {
      icon: <IconMaterialSymbolsBackspaceOutline />,
      onClick: () => editor.chain().focus().clearNodes().unsetAllMarks().run(),
    },
  };

  const groups = TOOL_GROUPS.map((group) => group.filter((tool) => tools.includes(tool))).filter(
    (group) => group.length > 0,
  );

  return (
    <div className="flex flex-wrap gap-2 border-b p-1">
      {groups.map((group, index) => (
        <Fragment key={group[0]}>
          {index > 0 && <div className="separator my-1 w-[1px] bg-black/10" />}
          <div className="flex gap-1">
            {group.map((name) => {
              const tool = toolButtons[name];
              return (
                <ToolbarButton
                  key={name}
                  onClick={tool.onClick}
                  disabled={tool.isDisabled ?? disabled}
                  isActive={tool.isActive ?? false}
                >
                  {tool.icon}
                </ToolbarButton>
              );
            })}
          </div>
        </Fragment>
      ))}
    </div>
  );
}

// Styling utilities recreated from legacy Field component
const inputTextAreaStyles =
  "px-2 py-1.5 flex-1 min-w-0 text-sm outline-0 my-1 mx-1 text-gray-800 dark:text-zinc-200 disabled:text-muted-foreground dark:disabled:text-zinc-600";

export function TiptapEditor({
  value = "",
  onChange,
  placeholder,
  disabled = false,
  editorOptions = {},
  id,
  className,
  contentClassName,
  tools = ALL_TOOLS,
  allowImages = false,
  ...props
}: TiptapEditorProps & {
  "data-testid"?: string;
}) {
  const [isFocused, setIsFocused] = useState(false);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false, // we'll configure it separately
      }),
      Heading.configure({
        levels: [1, 2, 3],
      }),
      TextAlign.configure({
        types: ["heading", "paragraph", "blockquote"],
        defaultAlignment: "left",
      }),
      Placeholder.configure({
        placeholder: placeholder,
      }),
      ...(allowImages ? [Image.configure({ allowBase64: true })] : []),
    ],
    content: value,
    editable: !disabled,
    editorProps: {
      attributes: {
        // The typography plugin isn't installed (prose is inert), so restore the list styles
        // that preflight strips — otherwise bullet/ordered lists render invisibly.
        class: cn(
          `tiptap prose prose-sm min-h-[70px] ${inputTextAreaStyles}`,
          "[&_ol]:list-decimal [&_ol]:pl-6 [&_ul]:list-disc [&_ul]:pl-6",
          contentClassName,
        ),
      },
      handleDOMEvents: {
        focus: () => {
          setIsFocused(true);
          return false;
        },
        blur: () => {
          setIsFocused(false);
          return false;
        },
      },
      ...(allowImages && {
        handlePaste: (view, event) => {
          const files = imageFilesFrom(event.clipboardData);
          if (files.length === 0) return false;
          event.preventDefault();
          for (const file of files) void insertImageFile(view, file);
          return true;
        },
        handleDrop: (view, event, _slice, moved) => {
          if (moved) return false;
          const files = imageFilesFrom(event.dataTransfer);
          if (files.length === 0) return false;
          event.preventDefault();
          const pos = view.posAtCoords({ left: event.clientX, top: event.clientY })?.pos;
          for (const file of files) void insertImageFile(view, file, pos);
          return true;
        },
      }),
    },
    onUpdate: ({ editor }) => {
      onChange?.(editor.getHTML());
    },
    // Fix for SSR hydration mismatch
    immediatelyRender: false,
    // Add placeholder support
    parseOptions: {
      preserveWhitespace: true,
    },
    ...editorOptions,
  });

  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      // Programmatic sync must not fire onChange: Tiptap normalizes HTML ("" -> "<p></p>"),
      // and emitting here marks pristine forms as changed (setContent emits by default in v3).
      editor.commands.setContent(value, { emitUpdate: false });
    }
  }, [editor, value]);

  return (
    <div
      data-slot="tiptap-editor"
      data-disabled={disabled}
      className={cn(
        // Base styles matching fieldGroupStyles
        "border-input border bg-white shadow-sm dark:bg-black/40",
        "bg-linear-to-br from-white to-black/2 dark:bg-none",
        "group flex h-auto items-center overflow-visible",
        "squircle rounded-lg supports-squircle:rounded-3xl",
        focusStyles,
        // Border styles matching fieldBorderStyles
        isFocused
          ? "ring-ring/50 border-ring ring-[3px] dark:ring-white/30"
          : "ring-black/10 hover:ring-black/20 dark:ring-white/10",
        disabled && "bg-muted/50 bg-none opacity-50 ring-black/7 hover:ring-black/7",
        "not-squircle block min-h-[120px]",
        className,
      )}
      {...props}
    >
      {editor && <MenuBar editor={editor} disabled={disabled} tools={tools} />}
      <EditorContent className="*:border-0 *:ring-0 *:outline-0" editor={editor} id={id} />
    </div>
  );
}

// Export the Editor type for consumers that need access to the full editor API
export type { Editor };
